import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Autocomplete,
  Box,
  Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axiosInstance from "../../utils/axiosInstance";
import axios from "axios";
import { toast } from "react-toastify";
import { getUIDescription } from "../SiteAdmin/customerGroupDetails/AssigningCustomerGroupDevice";

const AssignUnitDialog = ({
  open,
  onClose,
  groupId,
  groupName,
  fetchUnits,
}) => {
  const [selectedUnits, setSelectedUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [allUnits, setAllUnits] = useState([]);
  const [error, setError] = useState(null);

  const fetchAllUnits = async () => {
    if (!groupId) return;
    try {
      setLoading(true);
      setError(null);
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_GATEAWAY}/devices`
      );
      setAllUnits(response.data || []);
    } catch (err) {
      console.error("Error fetching units:", err);
      setError("Failed to load units.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchAllUnits();
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!groupId || selectedUnits.length === 0) {
      toast.warning("Please select at least one unit");
      return;
    }
    try {
      const payload = {
        deviceIds: selectedUnits,
        groupId,
        UI_Description: `${getUIDescription()}\n Assign Units to Report Group `,
      };
      await axiosInstance.post(
        `${process.env.REACT_APP_API_GATEAWAY}/assets/createMany/`,
        payload
      );
      toast.success("Successfully Assigned Units to Group Report!");
      setSelectedUnits([]);
      onClose();
      fetchUnits();
    } catch (err) {
      console.error("❌ Error to assign unit to report group:", err);
      toast.error("Failed to assign unit to report group");
    }
    onClose();
  };

  const handleCancel = () => {
    setSelectedUnits([]);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6" component="span">
          {`Assign Unit(s) To ${groupName}`}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={{ xs: 2, md: 4 }} my={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label="Group Name"
              value={groupName}
              disabled
              fullWidth
              sx={{ mb: 2 }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Autocomplete
              options={error ? [] : allUnits}
              getOptionLabel={(option) => option.name}
              value={allUnits.filter((unit) => selectedUnits.includes(unit.id))}
              onChange={(event, newValue) => {
                setSelectedUnits(newValue.map((unit) => unit.id)); // Store the selected unit ids in an array
              }}
              multiple
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Unit"
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {loading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
              loading={loading}
              disableClearable
            />
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button
            sx={{
              borderRadius: 0, // ✅ remove rounding
            }}
            onClick={handleCancel}
            color="secondary"
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            Assign
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AssignUnitDialog;
